import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar from "../../../components/Sidebar";
import CustomInput from "../../../components/CustomInput";
import CustomButton from "../../../components/CustomButton";
import CustomDropdown from "../../../components/CustomDropdown";
import { useToast } from "../../../contexts/ToastContext";
import { openSupportChatOnCurrentPage } from "../../../utils/supportChatNavigation";
import { fetchAgentClients } from "../../../controller/ibDashboard/clientsController";
import { sendNotifications } from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import "../../css/cachIn.css";

const RECEIVER_ALL = 0;
const RECEIVER_CUSTOM = 4;

const getClientNickName = (client) =>
  client?.NICK_NAME ||
  client?.NICK ||
  client?.nick ||
  client?.nick_name ||
  client?.NICKNAME ||
  "-";

export default function SendNotification() {
  const { t } = useTranslation("mobile");
  const { success: showSuccess, error: showError, warning: showWarning } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState({});

  const [title, setTitle] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const [receiverType, setReceiverType] = useState(RECEIVER_ALL);
  const [clients, setClients] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [recipientOption, setRecipientOption] = useState(null);

  const canProceed = useMemo(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    return !!token;
  }, []);

  const availableRecipientOptions = useMemo(
    () => clients.filter((client) => !selectedUsers.includes(client.value)),
    [clients, selectedUsers]
  );

  const selectedRecipientLabels = useMemo(() => {
    const labelsMap = new Map(clients.map((client) => [client.value, client.label]));
    return selectedUsers.map((id) => ({
      id,
      label: labelsMap.get(id) || String(id),
    }));
  }, [clients, selectedUsers]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const storedUserData = sessionStorage.getItem("userData");
        if (storedUserData) {
          try {
            setUser(JSON.parse(storedUserData));
          } catch (_) {}
        }

        const response = await fetchAgentClients({ page: 1, withDoc: 0, search: null });
        const rawList = Array.isArray(response?.DATA)
          ? response.DATA
          : Array.isArray(response?.DATA?.data)
          ? response.DATA.data
          : Array.isArray(response?.data)
          ? response.data
          : [];

        const normalized = rawList
          .map((c) => ({
            value: c.USER_ID ?? c.user_id ?? c.ID ?? c.id,
            label: String(getClientNickName(c)),
          }))
          .filter((c) => c.value != null && c.value !== "");

        setClients(normalized);
      } catch (error) {
        showError("Error Occurred", error?.message || "Failed to load contacts.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [showError]);

  const toggleUser = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
    if (errors.users) setErrors((prev) => ({ ...prev, users: null }));
  };

  const handleRecipientSelect = (option) => {
    setRecipientOption(option || null);
    if (!option?.value) return;
    toggleUser(option.value);
    setRecipientOption(null);
  };

  const validate = () => {
    const nextErrors = {};
    if (!title.trim()) nextErrors.title = "Title is required.";
    if (!messageBody.trim()) nextErrors.body = "Message body is required.";
    if (receiverType === RECEIVER_ALL && clients.length === 0) {
      nextErrors.users = "No clients available to receive this notification.";
    }
    if (receiverType === RECEIVER_CUSTOM && selectedUsers.length === 0) {
      nextErrors.users = "Please select at least one client.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSend = async () => {
    if (!validate()) return;
    try {
      setProcessing(true);
      const usersPayload =
        receiverType === RECEIVER_CUSTOM
          ? selectedUsers
              .map((id) => Number(id))
              .filter((id) => Number.isFinite(id))
          : [];
      const response = await sendNotifications({
        title,
        body: messageBody,
        receiver: receiverType,
        users: usersPayload,
      });
      const data = response?.decryptedData || response?.data || {};
      const code = Number(data?.CODE);

      if (code === AppConstants.responseOk || code === 200) {
        showSuccess("Success", "Notification sent successfully.");
        setTitle("");
        setMessageBody("");
        setSelectedUsers([]);
        setRecipientOption(null);
        return;
      }

      if (code === AppConstants.invalidInputs || code === 708) {
        showWarning("Action Required", "Please check title, body and selected recipients.");
        return;
      }

      showError("Error Occurred", data?.MESSAGE || `Failed to send notification (CODE: ${code || "Unknown"}).`);
    } catch (error) {
      showError("Error Occurred", error?.message || "Failed to send notification.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="cash-in-page cash-out-page">
      <Sidebar user={user} />
      <div className="cash-in-content">
        {loading && <div className="cash-in-loading-message">{t("loading")}...</div>}
        <div className="cash-in-container cash-in-container--styled">
          <div className="cash-in-tab-content cash-in-tab-content--styled">
            {!canProceed ? (
              <div className="cash-in-locked-message">
                <i className="fa-solid fa-lock"></i>
                <p>{t("you_dont_have_access_to_do_this_operation")}</p>
              </div>
            ) : (
              <div className="cash-in-layout-grid">
                <div className="cash-in-form-panel">
                  <div className="cash-in-panel-header">
                    <h2>Send Notification</h2>
                    <p>Send push notifications to your clients.</p>
                  </div>

                  <div className="cash-in-tabs send-notification-tabs">
                    <button
                      type="button"
                      className={`cash-in-tab send-notification-tab ${receiverType === RECEIVER_ALL ? "cash-in-tab--active" : ""}`}
                      onClick={() => {
                        setReceiverType(RECEIVER_ALL);
                        setRecipientOption(null);
                        if (errors.users) setErrors((prev) => ({ ...prev, users: null }));
                      }}
                      disabled={processing}
                    >
                      All Clients
                    </button>
                    <button
                      type="button"
                      className={`cash-in-tab send-notification-tab ${receiverType === RECEIVER_CUSTOM ? "cash-in-tab--active" : ""}`}
                      onClick={() => setReceiverType(RECEIVER_CUSTOM)}
                      disabled={processing}
                    >
                      Custom Clients
                    </button>
                  </div>
                  {receiverType === RECEIVER_ALL && errors.users && (
                    <div className="cash-in-document-error-message" style={{ marginBottom: 12 }}>
                      {errors.users}
                    </div>
                  )}

                  <div className="cash-in-form">
                    <div className="cash-in-form-group">
                      <label className="cash-in-form-label">Title</label>
                      <div className="cash-in-input-wrapper">
                        <span className="cash-in-input-icon">
                          <i className="fa-solid fa-heading"></i>
                        </span>
                        <CustomInput
                          type="text"
                          value={title}
                          onChange={(e) => {
                            setTitle(e.target.value);
                            if (errors.title) setErrors((prev) => ({ ...prev, title: null }));
                          }}
                          placeholder="Notification title"
                          className={`cash-in-input ${errors.title ? "cash-in-input-error" : ""}`}
                          disabled={processing}
                        />
                      </div>
                      {errors.title && <div className="cash-in-document-error-message">{errors.title}</div>}
                    </div>

                    <div className="cash-in-form-group">
                      <label className="cash-in-form-label">Message</label>
                      <textarea
                        value={messageBody}
                        onChange={(e) => {
                          setMessageBody(e.target.value);
                          if (errors.body) setErrors((prev) => ({ ...prev, body: null }));
                        }}
                        placeholder="Write your notification message"
                        className={`cash-in-input ${errors.body ? "cash-in-input-error" : ""}`}
                        disabled={processing}
                        rows={5}
                        style={{ width: "100%", resize: "vertical", minHeight: 110, borderRadius: 12, padding: 12 }}
                      />
                      {errors.body && <div className="cash-in-document-error-message">{errors.body}</div>}
                    </div>

                    {receiverType === RECEIVER_CUSTOM && (
                      <div className="cash-in-form-group col-12">
                        <label className="cash-in-form-label">Recipients</label>
                        <CustomDropdown
                          options={availableRecipientOptions}
                          value={recipientOption}
                          onChange={handleRecipientSelect}
                          placeholder={
                            availableRecipientOptions.length
                              ? "Search and select client"
                              : "No clients available"
                          }
                          className="cash-in-dropdown"
                          disabled={processing || availableRecipientOptions.length === 0}
                          searchable={true}
                        />
                        {selectedRecipientLabels.length > 0 && (
                          <div className="cash-in-method-list" style={{ maxHeight: 240, overflow: "auto", marginTop: 12 }}>
                            {selectedRecipientLabels.map((client) => (
                              <button
                                key={String(client.id)}
                                type="button"
                                className="cash-in-method-item cash-in-method-item--active"
                                onClick={() => toggleUser(client.id)}
                                disabled={processing}
                              >
                                <span className="cash-in-method-item-left">
                                  <span className="cash-in-method-radio is-selected" aria-hidden="true" />
                                  <i className="fa-solid fa-user"></i>
                                  <span>{client.label}</span>
                                </span>
                                <span className="cash-in-method-item-right">
                                  <i className="fa-solid fa-xmark"></i>
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                        {errors.users && <div className="cash-in-document-error-message">{errors.users}</div>}
                      </div>
                    )}
                  </div>

                  <div className="cash-in-actions">
                    <CustomButton
                      className="cash-in-proceed-button"
                      variant="primary"
                      onClick={handleSend}
                      disabled={processing}
                    >
                      <span>{processing ? "Sending..." : "Send Notification"}</span>
                    </CustomButton>
                  </div>
                </div>

                <div className="cash-in-side-panel">
                  <div className="cash-in-info-card">
                    <h3>
                      <i className="fa-solid fa-bell" />
                      Notification Summary
                    </h3>
                    <ul className="cash-in-processing-list">
                      <li>
                        <span>
                          <i className="fa-solid fa-users" />
                          Receiver Type
                        </span>
                        <strong>{receiverType === RECEIVER_ALL ? "All Clients" : "Custom"}</strong>
                      </li>
                      <li>
                        <span>
                          <i className="fa-solid fa-user-check" />
                          Selected Users
                        </span>
                        <strong>{receiverType === RECEIVER_ALL ? "Auto" : selectedUsers.length}</strong>
                      </li>
                    </ul>
                  </div>

                  <div className="cash-in-help-card">
                    <h3>
                      <i className="fa-solid fa-headset" />
                      {t("contactus_title")}
                    </h3>
                    <p>Need help sending notifications?</p>
                    <button
                      type="button"
                      onClick={() =>
                        openSupportChatOnCurrentPage(
                          navigate,
                          location.pathname,
                          location.search
                        )
                      }
                    >
                      {t("contact_us")}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

